/**
 * The lane chips across the top of the portfolio (JARVIS-126): one
 * `Tag.CheckableTag` per category plus the ⚡ Priority chip. Checked chips
 * filter the grid (union; none checked = everything). Category chips are also
 * drop zones — dropping a tile on one moves it to that category, through the
 * limit-checked `updateProject({ category })`.
 */
import { ThunderboltFilled } from "@ant-design/icons"
import { Tag, Tooltip } from "antd"
import { type DragEvent, useState } from "react"
import styled from "styled-components"

import {
  PORTFOLIO_CATEGORY_ORDER,
  type PortfolioLaneChip,
  PRIORITY_LANE_CHIP,
} from "#components/PortfolioManagement/portfolioArrangement"
import {
  categoryLabelMap,
  categoryTextColorMap,
} from "#components/PortfolioManagement/portfolioPhotoGridConstants"
import {
  PORTFOLIO_CATEGORY,
  PORTFOLIO_CATEGORY_LIMITS,
  type PortfolioCategory,
} from "#customTypes/composedTypes/portfolioTypes"

const SRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;

  & .ant-tag-checkable {
    border: 1px solid #d9d9d9;
    padding: 2px 10px;
    font-size: 13px;
    line-height: 20px;
    user-select: none;
  }
  & .ant-tag-checkable-checked {
    border-color: transparent;
  }
  & .ant-tag-checkable[data-drop-target="true"] {
    outline: 2px dashed #1677ff;
    outline-offset: 1px;
  }
`

export interface PortfolioLaneChipsProps {
  checked: ReadonlySet<PortfolioLaneChip>
  onToggle: (chip: PortfolioLaneChip) => void
  countsByCategory: Record<PortfolioCategory, number>
  laneCount: number
  laneMax: number
  /** True while a project tile is being dragged; enables the drop zones. */
  isDragActive: boolean
  /** The dragged project's category, so its own chip does not light up. */
  dragCategory: PortfolioCategory | null
  onDropOnCategory: (category: PortfolioCategory) => void
  onDropOnPriority: () => void
}

const PortfolioLaneChips = ({
  checked,
  onToggle,
  countsByCategory,
  laneCount,
  laneMax,
  isDragActive,
  dragCategory,
  onDropOnCategory,
  onDropOnPriority,
}: PortfolioLaneChipsProps) => {
  const [dropTarget, setDropTarget] = useState<PortfolioLaneChip | null>(null)

  const canDropOn = (chip: PortfolioLaneChip): boolean => {
    if (!isDragActive) return false
    if (chip === PRIORITY_LANE_CHIP) return true
    // Unassigned is fixed in both directions; same-category drops are no-ops.
    if (chip === PORTFOLIO_CATEGORY.UNASSIGNED) return false
    if (dragCategory === PORTFOLIO_CATEGORY.UNASSIGNED) return false
    return chip !== dragCategory
  }

  const dropHandlers = (chip: PortfolioLaneChip) => ({
    onDragOver: (event: DragEvent<HTMLElement>) => {
      if (!canDropOn(chip)) return
      event.preventDefault()
      event.dataTransfer.dropEffect = "move"
      setDropTarget(chip)
    },
    onDragLeave: () => setDropTarget((prev) => (prev === chip ? null : prev)),
    onDrop: (event: DragEvent<HTMLElement>) => {
      if (!canDropOn(chip)) return
      event.preventDefault()
      event.stopPropagation()
      setDropTarget(null)
      if (chip === PRIORITY_LANE_CHIP) onDropOnPriority()
      else onDropOnCategory(chip)
    },
  })

  return (
    <SRow
      role="group"
      aria-label="Lane filters"
      data-testid="portfolio-lane-chips"
    >
      {PORTFOLIO_CATEGORY_ORDER.map((category) => {
        const limit = PORTFOLIO_CATEGORY_LIMITS[category]
        const count = countsByCategory[category] ?? 0
        const countLabel = Number.isFinite(limit)
          ? `${count}/${limit}`
          : `${count}`
        const isChecked = checked.has(category)
        return (
          <Tooltip
            key={category}
            title={
              Number.isFinite(limit)
                ? `${categoryLabelMap[category]}: ${count} of ${limit} — drop a project here to move it`
                : category === PORTFOLIO_CATEGORY.UNASSIGNED
                  ? "Artwork the scanner added that has not been placed yet"
                  : `${categoryLabelMap[category]} — drop a project here to move it`
            }
          >
            <Tag.CheckableTag
              checked={isChecked}
              onChange={() => onToggle(category)}
              data-testid={`portfolio-chip-${category}`}
              data-checked={isChecked || undefined}
              data-drop-target={dropTarget === category || undefined}
              style={
                isChecked
                  ? {
                      background: categoryTextColorMap[category],
                      color: "#fff",
                    }
                  : { color: categoryTextColorMap[category] }
              }
              {...dropHandlers(category)}
            >
              {categoryLabelMap[category]} · {countLabel}
            </Tag.CheckableTag>
          </Tooltip>
        )
      })}
      <Tooltip title="Projects in the priority lane — drop a project here to add it">
        <Tag.CheckableTag
          checked={checked.has(PRIORITY_LANE_CHIP)}
          onChange={() => onToggle(PRIORITY_LANE_CHIP)}
          data-testid="portfolio-chip-priority"
          data-checked={checked.has(PRIORITY_LANE_CHIP) || undefined}
          data-drop-target={dropTarget === PRIORITY_LANE_CHIP || undefined}
          style={
            checked.has(PRIORITY_LANE_CHIP)
              ? { background: "#ad6800", color: "#fff" }
              : { color: "#ad6800" }
          }
          {...dropHandlers(PRIORITY_LANE_CHIP)}
        >
          <ThunderboltFilled /> Priority · {laneCount}/{laneMax}
        </Tag.CheckableTag>
      </Tooltip>
    </SRow>
  )
}

export default PortfolioLaneChips
